import { useEffect, useState, type ReactElement } from "react";
import { fetchUsers } from "../api/client";
import { useAccessToken } from "../auth/useAccessToken";
import { ConfigurationRequired } from "../shell/ConfigurationRequired";
import { PageHeader } from "../shell/PageHeader";
import { readConfig } from "../config";
import { Callout, Spinner, Stack, Surface } from "../ui-kit";
import { MetricWidget } from "../dashboard/MetricWidget";
import styles from "./UsersView.module.css";

type UsersRequestState =
  | { status: "loading" }
  | { status: "success"; count: number }
  | { status: "error" };

/**
 * The users metric (ISSUE-455 Deliverable 3). `GET /users` now returns a
 * single mobile-customer count, not a roster (ADR-052 §3 amended
 * 2026-09-18) — this fires one request on route load via `fetchUsers`, same
 * no-automatic-retry, no-manual-retry-control contract the prior roster view
 * used (design spec: `docs/design/list-view-request-states.md`). `cancelled`
 * guards against a stale request committing state after this effect's owner
 * unmounts or reruns for a new `getAccessToken` identity.
 */
export function UsersView(): ReactElement {
  const config = readConfig();
  const getAccessToken = useAccessToken();
  const [state, setState] = useState<UsersRequestState>({ status: "loading" });

  useEffect(() => {
    if (!config.ok) {
      return;
    }
    let cancelled = false;
    setState({ status: "loading" });
    fetchUsers(getAccessToken).then(
      (response) => {
        if (!cancelled) {
          setState({ status: "success", count: response.count });
        }
      },
      () => {
        if (!cancelled) {
          setState({ status: "error" });
        }
      },
    );
    return (): void => {
      cancelled = true;
    };
  }, [config.ok, getAccessToken]);

  return (
    <Stack gap={6}>
      <PageHeader
        title="Users"
        description="The number of mobile customers with at least one indexed document. Telegram is out
          of scope on this surface (ADR-052 §3)."
      />
      {config.ok && state.status === "success" ? (
        <MetricWidget title="Пользователи" value={String(state.count)} titleAs="h2" />
      ) : (
        <Surface as="section" aria-label="Users" padding={config.ok ? "none" : "default"}>
          {!config.ok ? (
            <ConfigurationRequired missing={config.missing} />
          ) : state.status === "loading" ? (
            <div className={styles.stateRegion}>
              <Spinner label="Loading users…" showLabel />
            </div>
          ) : (
            <div className={styles.stateRegion}>
              <Callout tone="danger" role="alert" title="Users could not be loaded">
                User data could not be loaded. No data is being shown.
              </Callout>
            </div>
          )}
        </Surface>
      )}
    </Stack>
  );
}
